// src/math.ts
function rand(min, max) {
  return min + Math.random() * (max - min);
}
function lerp_number(t, s, e) {
  if (t <= 0) return s;
  if (t >= 1) return e;
  return s + (e - s) * t;
}
function range(count) {
  let list = [];
  for (let i = 0; i < count; i++) {
    list.push(i);
  }
  return list;
}
var Point = class _Point {
  constructor(x, y) {
    this.x = x;
    this.y = y;
  }
  add(pt) {
    return new _Point(this.x + pt.x, this.y + pt.y);
  }
  subtract(pt) {
    return new _Point(this.x - pt.x, this.y - pt.y);
  }
  multiply(point) {
    return new _Point(this.x * point.x, this.y * point.y);
  }
  magnitude() {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }
  unit() {
    return this.scale(1 / this.magnitude());
  }
  scale(scale) {
    return new _Point(this.x * scale, this.y * scale);
  }
  distance(pt) {
    return this.subtract(pt).magnitude();
  }
  floor() {
    return new _Point(Math.floor(this.x), Math.floor(this.y));
  }
  clamp(min, max) {
    let x = this.x;
    if (x < min.x) x = min.x;
    if (x > max.x) x = max.x;
    let y = this.y;
    if (y < min.y) y = min.y;
    if (y > max.y) y = max.y;
    return new _Point(x, y);
  }
  lerp(t, that) {
    return new _Point(
      lerp_number(t, this.x, that.x),
      lerp_number(t, this.y, that.y)
    );
  }
  copy() {
    return new _Point(this.x, this.y);
  }
  toString() {
    return `Point(${this.x.toFixed(1)},${this.y.toFixed(1)})`;
  }
  toJSON() {
    return {
      x: this.x,
      y: this.y
    };
  }
  static fromJSON(point) {
    return new _Point(point.x, point.y);
  }
};
var Size = class _Size {
  constructor(w, h) {
    this.w = w;
    this.h = h;
  }
  set(w, h) {
    this.w = w;
    this.h = h;
  }
  addPoint(pt) {
    return new _Size(this.w + pt.x, this.h + pt.y);
  }
  scale(scale) {
    return new _Size(this.w * scale, this.h * scale);
  }
  asPoint() {
    return new Point(this.w, this.h);
  }
  growInsets(insets) {
    return new _Size(
      this.w + insets.left + insets.right,
      this.h + insets.top + insets.bottom
    );
  }
  copy() {
    return new _Size(this.w, this.h);
  }
  toString() {
    return `Size(${this.w} x ${this.h})`;
  }
  toJSON() {
    return {
      w: this.w,
      h: this.h
    };
  }
  static fromJSON(size) {
    return new _Size(size.w, size.h);
  }
};
var Insets = class _Insets {
  constructor(top, right, bottom, left) {
    this.top = top;
    this.right = right;
    this.bottom = bottom;
    this.left = left;
  }
  copy() {
    return new _Insets(this.top, this.right, this.bottom, this.left);
  }
  add(insets) {
    return new _Insets(
      this.top + insets.top,
      this.right + insets.right,
      this.bottom + insets.bottom,
      this.left + insets.left
    );
  }
  width() {
    return this.left + this.right;
  }
  height() {
    return this.top + this.bottom;
  }
  isEmpty() {
    return this.left <= 0 && this.right <= 0 && this.top <= 0 && this.bottom <= 0;
  }
  toString() {
    return `Insets(${this.top.toFixed(1)}, ${this.right.toFixed(
      1
    )}, ${this.bottom.toFixed(1)}, ${this.left.toFixed(1)})`;
  }
  toJSON() {
    return {
      top: this.top,
      right: this.right,
      bottom: this.bottom,
      left: this.left
    };
  }
  static from(value) {
    return new _Insets(value, value, value, value);
  }
  static fromJSON(insets) {
    return new _Insets(insets.top, insets.right, insets.bottom, insets.left);
  }
};
var Bounds = class _Bounds {
  constructor(x, y, w, h) {
    this.x = x;
    this.y = y;
    this.w = w;
    this.h = h;
  }
  get x2() {
    return this.x + this.w;
  }
  get y2() {
    return this.y + this.h;
  }
  top() {
    return this.y;
  }
  top_left() {
    return new Point(this.x, this.top());
  }
  top_midpoint() {
    return new Point(this.x + this.w / 2, this.top());
  }
  top_right() {
    return new Point(this.x + this.w, this.top());
  }
  left() {
    return this.x;
  }
  left_midpoint() {
    return new Point(this.left(), this.y + this.h / 2);
  }
  bottom() {
    return this.y + this.h;
  }
  bottom_left() {
    return new Point(this.x, this.bottom());
  }
  bottom_midpoint() {
    return new Point(this.x + this.w / 2, this.bottom());
  }
  bottom_right() {
    return new Point(this.x + this.w, this.bottom());
  }
  right() {
    return this.x + this.w;
  }
  right_midpoint() {
    return new Point(this.right(), this.y + this.h / 2);
  }
  center() {
    return new Point(this.x + this.w / 2, this.y + this.h / 2);
  }
  position() {
    return this.top_left();
  }
  size() {
    return new Size(this.w, this.h);
  }
  set(x, y, w, h) {
    this.x = x;
    this.y = y;
    this.w = w;
    this.h = h;
  }
  add_self(point) {
    this.x += point.x;
    this.y += point.y;
  }
  add(point) {
    return new _Bounds(this.x + point.x, this.y + point.y, this.w, this.h);
  }
  contains(pt) {
    if (pt.x < this.x) return false;
    if (pt.y < this.y) return false;
    if (pt.x > this.x + this.w) return false;
    if (pt.y > this.y + this.h) return false;
    return true;
  }
  intersects(other) {
    if (this.left() >= other.right()) return false;
    if (this.right() <= other.left()) return false;
    if (this.top() >= other.bottom()) return false;
    if (this.bottom() <= other.top()) return false;
    return true;
  }
  asInsets() {
    return new Insets(this.top(), this.right(), this.bottom(), this.left());
  }
  grow(v) {
    return new _Bounds(this.x - v, this.y - v, this.w + v * 2, this.h + v * 2);
  }
  growInsets(insets) {
    return new _Bounds(
      this.x - insets.left,
      this.y - insets.top,
      this.w + insets.left + insets.right,
      this.h + insets.top + insets.bottom
    );
  }
  shrinkInsets(insets) {
    return new _Bounds(
      this.x + insets.left,
      this.y + insets.top,
      this.w - insets.left - insets.right,
      this.h - insets.top - insets.bottom
    );
  }
  scale(s) {
    return new _Bounds(this.x * s, this.y * s, this.w * s, this.h * s);
  }
  copy() {
    return new _Bounds(this.x, this.y, this.w, this.h);
  }
  toString() {
    return `Bounds(${this.x.toFixed(1)}, ${this.y.toFixed(
      1
    )}) x (${this.w.toFixed(1)},${this.h.toFixed(1)})`;
  }
  toJSON() {
    return {
      x: this.x,
      y: this.y,
      w: this.w,
      h: this.h
    };
  }
  withPosition(point) {
    return new _Bounds(point.x, point.y, this.w, this.h);
  }
  withSize(size) {
    return new _Bounds(this.x, this.y, size.w, size.h);
  }
  static fromPointSize(point, size) {
    return new _Bounds(point.x, point.y, size.w, size.h);
  }
  static fromJSON(bounds) {
    return new _Bounds(bounds.x, bounds.y, bounds.w, bounds.h);
  }
};
var genId = (prefix) => `${prefix}_${Math.floor(Math.random() * 1e6)}`;

// src/jarray.ts
var JArray = class _JArray extends Array {
  static of(...args) {
    return new _JArray(...args);
  }
  static range(first) {
    let arr = new _JArray();
    for (let i = 0; i < first; i++) arr.push(i);
    return arr;
  }
  constructor(...args) {
    super(...args);
  }
  map(fn) {
    return super.map(fn);
  }
  tap(fn) {
    this.forEach(fn);
    return this;
  }
  filter(fn) {
    return super.filter(fn);
  }
  pluck(name) {
    return this.map((x) => x[name]);
  }
  first() {
    return this[0];
  }
  last() {
    return this[this.length - 1];
  }
  empty() {
    return this.length === 0;
  }
};

// src/arraygrid.ts
var ArrayGrid = class _ArrayGrid {
  constructor(w, h) {
    this.w = w;
    this.h = h;
    this.data = [];
  }
  set_at(x, y, value) {
    if (x < 0) return void 0;
    if (y < 0) return void 0;
    if (x >= this.w) return void 0;
    if (y >= this.h) return void 0;
    let n = y * this.w + x;
    this.data[n] = value;
  }
  set(n, value) {
    this.set_at(n.x, n.y, value);
  }
  get_at(x, y) {
    if (x < 0) {
      return void 0;
    }
    if (y < 0) {
      return void 0;
    }
    if (x >= this.w) {
      return void 0;
    }
    if (y >= this.h) {
      return void 0;
    }
    let n = y * this.w + x;
    return this.data[n];
  }
  get(n) {
    return this.get_at(n.x, n.y);
  }
  isValidIndex(pt) {
    if (pt.x < 0) return false;
    if (pt.y < 0) return false;
    if (pt.x >= this.w) return false;
    if (pt.y >= this.h) return false;
    return true;
  }
  forEach(cb) {
    for (let j = 0; j < this.h; j++) {
      for (let i = 0; i < this.w; i++) {
        let value = this.get_at(i, j);
        cb(value, new Point(i, j));
      }
    }
  }
  fill(cb) {
    for (let j = 0; j < this.h; j++) {
      for (let i = 0; i < this.w; i++) {
        let v = cb(new Point(i, j));
        this.set_at(i, j, v);
      }
    }
  }
  size() {
    return this.data.length;
  }
  set_from_list(list) {
    for (let j = 0; j < this.h; j++) {
      for (let i = 0; i < this.w; i++) {
        let n = this.w * j + i;
        this.set_at(i, j, list[n]);
      }
    }
  }
  static fromPattern(pattern, cb) {
    let lines = pattern.trim().split("\n").map((l) => l.trim());
    let h = lines.length;
    let w = lines[0].length;
    let grid = new _ArrayGrid(w, h);
    let y = 0;
    for (let line of lines) {
      line = line.trim();
      let x = 0;
      for (let ch of line) {
        grid.set_at(x, y, cb(ch, new Point(x, y)));
        x++;
      }
      y++;
    }
    return grid;
  }
  static fromSize(size) {
    return new _ArrayGrid(size.w, size.h);
  }
  static fromArray(size, array) {
    let grid = _ArrayGrid.fromSize(size);
    grid.fill((pt) => {
      let n = pt.x + pt.y * size.w;
      return array[n];
    });
    return grid;
  }
};

// src/index.ts
var ConsoleLogger = class {
  constructor(prefix) {
    this.prefix = prefix;
    this.enabled = true;
  }
  setEnabled(enabled) {
    this.enabled = enabled;
  }
  error(...args) {
    if (this.enabled) console.error(`${this.prefix}:ERROR`, ...args);
  }
  info(...args) {
    if (this.enabled) console.info(`${this.prefix}:INFO`, ...args);
  }
  warn(...args) {
    if (this.enabled) console.warn(`${this.prefix}:WARN`, ...args);
  }
  assert(cond, msg) {
    if (!cond) throw new Error(`${this.prefix}:ASSERT FAILED ${msg}`);
    if (this.enabled) console.log(`${this.prefix}:ASSERT`, msg);
  }
};
function make_logger(prefix) {
  return new ConsoleLogger(prefix ? prefix : "");
}
function sleep(sec) {
  return new Promise((res, _rej) => {
    setTimeout(() => res(), Math.floor(sec * 1e3));
  });
}
function toRadians(param) {
  return Math.PI * 2 / 360 * param;
}
function pick(arr) {
  let n = Math.floor(Math.random() * arr.length);
  return arr[n];
}
export {
  ArrayGrid,
  Bounds,
  Insets,
  JArray,
  Point,
  Size,
  genId,
  lerp_number,
  make_logger,
  pick,
  rand,
  range,
  sleep,
  toRadians
};
